import React, { useCallback, useEffect, useState } from "react";
import api, { fileUrl } from "../lib/api";

const fmt = (n) => (n || 0).toLocaleString();
const mmss = (s) => `${Math.floor((s || 0) / 60)}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;

function PkSide({ side, score, win, align }) {
  return (
    <div className={`flex items-center gap-2 flex-1 min-w-0 ${align === "right" ? "flex-row-reverse" : ""}`}>
      <div className={`w-9 h-9 shrink-0 rounded-full overflow-hidden bg-white/15 flex items-center justify-center border-2 ${win ? "border-jam-gold" : "border-transparent"}`}>
        {side?.avatar_path ? <img src={fileUrl(side.avatar_path)} alt="" className="w-full h-full object-cover" /> : <span className="text-cream font-black text-xs">{(side?.username || "?")[0]?.toUpperCase()}</span>}
      </div>
      <div className={`min-w-0 ${align === "right" ? "text-right" : ""}`}>
        <p className="text-cream text-[11px] font-black truncate max-w-[90px]">@{side?.username}</p>
        <p className="text-jam-gold text-sm font-black">{fmt(score)}</p>
      </div>
    </div>
  );
}

export default function PkBattleBar({ sessionId, myUserId, isHost }) {
  const [pk, setPk] = useState(null);
  const [ending, setEnding] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/live/${sessionId}/pk`); setPk(data.pk); } catch { /* ignore */ }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    load();
    const t = setInterval(load, 2500);
    return () => clearInterval(t);
  }, [sessionId, load]);

  if (!pk) return null;
  const participant = pk.host_a?.id === myUserId || pk.host_b?.id === myUserId;

  if (pk.status === "invited") {
    if (!participant) return null;
    return <div data-testid="pk-battle-bar" className="crystal-glass rounded-2xl px-4 py-2 text-cream text-xs font-black">⚔️ PK invite sent — waiting for @{pk.host_b?.username}…</div>;
  }
  if (pk.status === "ended") {
    const label = pk.winner_side === "draw" ? "PK DRAW" : `👑 @${pk.winner_side === "a" ? pk.host_a?.username : pk.host_b?.username} WON`;
    return (
      <div data-testid="pk-battle-bar" className="crystal-glass rounded-2xl px-4 py-3 text-center border border-jam-gold/50">
        <p data-testid="pk-result" className="text-jam-gold font-black">{label}</p>
        <p className="text-cream/70 text-xs mt-1">{fmt(pk.score_a)} — {fmt(pk.score_b)}</p>
      </div>
    );
  }
  const countdown = pk.phase === "countdown";
  const endPk = async () => { setEnding(true); try { await api.post(`/live/pk/${pk.id}/end`); await load(); } catch { /* ignore */ } finally { setEnding(false); } };
  return (
    <div data-testid="pk-battle-bar" className="crystal-glass rounded-2xl px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <PkSide side={pk.host_a} score={pk.score_a} win={pk.winner_side === "a"} align="left" />
        <div className="flex flex-col items-center min-w-[56px]">
          {countdown ? (
            <span data-testid="pk-countdown" className="text-jam-gold text-2xl font-black">{pk.countdown_left ?? 0}</span>
          ) : (
            <>
              <span className="text-cream font-black text-sm tracking-widest">VS</span>
              <span data-testid="pk-timer" className="text-cream/80 text-[11px] font-bold">⏱ {mmss(pk.time_left)}</span>
            </>
          )}
        </div>
        <PkSide side={pk.host_b} score={pk.score_b} win={pk.winner_side === "b"} align="right" />
      </div>
      <div data-testid="pk-meter" className="flex h-2.5 rounded-full overflow-hidden mt-2 bg-white/10">
        <div className="h-full bg-papaya transition-all duration-500" style={{ width: `${pk.meter_a}%` }} />
        <div className="h-full bg-jam-gold transition-all duration-500" style={{ width: `${pk.meter_b}%` }} />
      </div>
      {isHost && pk.status === "active" ? (
        <button onClick={endPk} disabled={ending} data-testid="pk-end-btn" className="mt-2 mx-auto block px-4 py-1.5 rounded-full bg-red-500/90 text-white text-[11px] font-black">{ending ? "…" : "End PK"}</button>
      ) : null}
    </div>
  );
}
